/**
 * Planning Chart: 30m candles with per-candle Volume Profile + TPO.
 *
 * Reuses the same chart implementation as /chart (ESChart) so it
 * shows the 30m candlesticks plus the session volume profile and session TPO,
 * with the per-candle mini-profiles layered on top. Locked to 30m.
 *
 * Indicators are driven by the shared IndicatorSidePanel with a curated config
 * (see ./planningIndicators): Per-candle Profiles, Volume (session VP/TPO with a
 * unified Full/ETH-RTH/RTH Session picker, TPO bars only), Options / OI and
 * Session Levels. Panel + indicator state persist under their own localStorage
 * keys.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ESChart, IndicatorSidePanel } from '../components';
import { useLiveCandles, useLevelIndicators } from '../hooks';
import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { getLatestSessionDate, snapToWeekday } from '../utils/market';
import { PLANNING_GROUPS, PLANNING_INDICATOR_DEFAULTS } from './planningIndicators';
import { OVERLAY_STATE_IDS, buildQuoteOverlays } from '../indicators/underlyingOverlays';
import '../components/NavigationBar.css';

const TF = '30m';
const CANDLE_BODY_PX = 5;  // fixed thin candle body width
const BAR_SPACING = 18;    // fixed separation so per-candle profiles have room

export default function PlanningChartPage() {
  // End pinned to the latest session (where live updates happen); start is the
  // user-selectable left edge of the visible range. Default both to the latest
  // session (today), like the main chart; the From picker can still be moved
  // earlier to scroll back through history.
  // endDate is STATE (not a render-time const) so the chart can follow the
  // 18:00 ET session roll without a manual reload - a timer below re-evaluates
  // getLatestSessionDate() and advances it.
  const [endDate, setEndDate] = useState(getLatestSessionDate);  // lazy init: called once
  const defaultStart = endDate;  // recomputed each render -> always the latest session
  const [startDate, setStartDate] = useState(defaultStart);

  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);
  // Loading states for the GEX/OI plugin and OI baselines (Options / OI group)
  const [greeksLoading, setGreeksLoading] = useState(false);
  const [baselineLoading, setBaselineLoading] = useState(false);
  // Reserve chart space for the indicator panel so the price axis is not covered
  const [panelOpen, setPanelOpen] = useState(false);

  // Global grid-lines setting (edited in the main chart's Candle Colors dialog);
  // read once at mount, same static treatment this page gives candle colors.
  const [hideGridLines] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hideGridLines') || 'false') === true; } catch (e) { return false; }
  });

  // Indicator toggle state (persisted to its own localStorage key, independent
  // of the main chart's `indicators`).
  const [indicators, setIndicators] = useState(() => {
    try {
      const saved = localStorage.getItem('planningIndicators');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...PLANNING_INDICATOR_DEFAULTS, ...parsed };
      }
    } catch (e) { /* ignore */ }
    return PLANNING_INDICATOR_DEFAULTS;
  });
  const handleIndicatorToggle = useCallback((id, value) => {
    setIndicators((prev) => {
      const next = { ...prev, [id]: value };
      try { localStorage.setItem('planningIndicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);
  // Bulk apply a saved preset snapshot from the side panel (one atomic merge).
  const handleApplyIndicators = useCallback((snapshot) => {
    setIndicators((prev) => {
      const next = { ...prev, ...snapshot };
      try { localStorage.setItem('planningIndicators', JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, []);

  // Quote-overlay prop for the chart (registry-driven; scalar-key dep so
  // the memo never keys on the indicators object identity).
  const overlayStateKey = OVERLAY_STATE_IDS.map((id) => (indicators[id] ? '1' : '0')).join('');
  const quoteOverlays = useMemo(
    () => buildQuoteOverlays(indicators),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overlayStateKey]);

  const updateCandleRef = useRef(null);
  // Per-horizon refit health of the live-curve rows (side-panel state line)
  const [liveCurvesHealth, setLiveCurvesHealth] = useState(null);

  // Session-level + OI-level data, fetched only when a relevant indicator is on.
  // Session levels: Prior/Asia-London/Opening Range/etc. Zero levels
  // (zeroLevels) and Expected Move, keyed by expiry mode + underlying.
  const anySessionLevel = indicators.priorLevels ||
    (indicators.overnightLevel && (indicators.asiaLevels || indicators.londonLevels)) ||
    indicators.openingRange || indicators.initialBalance ||
    indicators.openingPrints || indicators.pivotPoints || indicators.sessionMarkers;
  const anyZeroLevel = indicators.zeroLevels;
  const emEnabled = indicators.expectedMove;
  const anyGammaLevel = indicators.gammaLevels;
  const levelIndicators = useLevelIndicators(startDate, {
    endDate,
    expiryMode: indicators.expiryMode,
    underlying: indicators.underlying,
    enabled: anySessionLevel || anyZeroLevel || emEnabled || anyGammaLevel,
    fetchSession: anySessionLevel,
    fetchZero: anyZeroLevel,
    fetchEm: emEnabled,
    fetchGamma: anyGammaLevel,
  });

  // Fetch the 30m candle range
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `${CONFIG.apiUrl}/api/es/candles?start_date=${startDate}&timeframe=${TF}&end_date=${endDate}`;
        const resp = await authFetch(url);
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled && data.candles) setCandles(data.candles);
        }
      } catch (e) {
        console.error('[Planning] candle fetch failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate]);

  // Follow the 18:00 ET session roll without a manual reload. getLatestSessionDate()
  // rolls to the next session after 18:00 ET; poll it and advance endDate when it
  // changes (the fetch effect + useLevelIndicators + chart props all re-run off it).
  // Snap startDate to the new session ONLY if the user is still pinned to the latest
  // session; if they scrolled back into history, leave their range alone. endDateRef +
  // functional setStartDate avoid stale-closure reads with the empty deps array.
  const endDateRef = useRef(endDate);
  useEffect(() => {
    const checkRoll = () => {
      const next = getLatestSessionDate();
      const prevEnd = endDateRef.current;
      if (next === prevEnd) return;  // no roll -> no state churn
      endDateRef.current = next;
      setEndDate(next);
      setStartDate((prevStart) => (prevStart === prevEnd ? next : prevStart));
    };
    const id = setInterval(checkRoll, 15000);  // detect roll within ~15s of 18:00 ET
    return () => clearInterval(id);
  }, []);

  // Live 30m candle updates (price series stays current; profiles self-update via SSE)
  const handleLive = useCallback((candle, isComplete) => {
    if (updateCandleRef.current) updateCandleRef.current(candle, isComplete);
  }, []);
  const handleBulk = useCallback((bulk) => {
    if (Array.isArray(bulk) && bulk.length) {
      setCandles((prev) => {
        const m = new Map(prev.map((c) => [c.time, c]));
        bulk.forEach((c) => m.set(c.time, c));
        return Array.from(m.values()).sort((a, b) => a.time - b.time);
      });
    }
  }, []);
  useLiveCandles(handleLive, handleBulk, TF, true);

  const onChartReady = useCallback((chart, series, container, updateCandle) => {
    updateCandleRef.current = updateCandle;
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0f1420', padding: 10, gap: 8 }}>
      {/* Header / controls */}
      <div className="chart-subbar">
        <Link to="/chart" className="nav-link">← Chart</Link>
        <span className="chart-subbar-title">Planning · 30m profiles</span>
        <label className="chart-subbar-label">
          From
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => {
              const d = snapToWeekday(e.target.value);
              if (d) setStartDate(d <= endDate ? d : endDate);
            }}
            className="nav-date"
          />
        </label>
        {startDate !== defaultStart && (
          <button
            onClick={() => setStartDate(defaultStart)}
            className="nav-btn"
          >
            Latest
          </button>
        )}
        <span className="chart-subbar-status">→ {endDate}{loading ? ' · loading…' : ''}</span>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, borderRadius: 10, overflow: 'hidden', border: '1px solid #2a3344', background: '#1a1f2e', position: 'relative', marginRight: panelOpen ? 300 : 0, transition: 'margin-right 0.28s cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <ESChart
          candles={candles}
          date={endDate}
          endDate={endDate}
          timeframe={TF}
          timezone="ET"
          hideGridLines={hideGridLines}
          expiryMode={indicators.expiryMode}
          underlying={indicators.underlying}
          onChartReady={onChartReady}
          onGreeksLoadingChange={setGreeksLoading}
          baselineLoading={baselineLoading}
          onBaselineLoadingChange={setBaselineLoading}
          // Per-candle profiles
          showCandleProfiles={indicators.candleProfiles}
          // Full session (ETH) so every candle shown gets a profile (no session picker)
          candleProfileSessionType="eth"
          showCandleProfileVolume={indicators.candleProfileVolume}
          showCandleProfileTpo={indicators.candleProfileTpo}
          // Session profiles (TPO bars only; shared Full/ETH-RTH/RTH Session picker)
          showVolumeProfile={indicators.volumeProfile}
          showNakedPOC={indicators.nakedPOC}
          showNakedTpoPOC={indicators.nakedTpoPOC}
          showHVNZones={indicators.hvnZones}
          vpSessionMode={indicators.vpSessionScope}
          showDeltaProfile={indicators.deltaProfile}
          showTPOProfile={indicators.tpoProfile}
          tpoRenderMode="histogram"
          tpoSessionScope={indicators.tpoSessionScope}
          tpoDisplayStyle="basic"
          showVWAP={indicators.vwap}
          showVWAPBand1={indicators.vwapBand1}
          showVWAPBand2={indicators.vwapBand2}
          vwapSessionMode={indicators.vwapSessionMode || 'full'}
          showCVD={indicators.cvd}
          // Session levels
          showPrevDayLevels={indicators.priorLevels && indicators.prevDayLevels}
          showPriorWeekLevels={indicators.priorLevels && indicators.priorWeekLevels}
          showPriorMonthLevels={indicators.priorLevels && indicators.priorMonthLevels}
          showAsiaLevels={indicators.overnightLevel && indicators.asiaLevels}
          showLondonLevels={indicators.overnightLevel && indicators.londonLevels}
          showOpeningRange={indicators.openingRange}
          showInitialBalance={indicators.initialBalance}
          showOpeningPrints={indicators.openingPrints}
          showPivotPoints={indicators.pivotPoints}
          showSessionMarkers={indicators.sessionMarkers}
          levelData={levelIndicators}
          // Options / OI
          showZeroLevels={indicators.zeroLevels}
          zeroLevelGreeks={{ gex: indicators.zeroLevelsGex ?? true, dex: indicators.zeroLevelsDex ?? true, vex: indicators.zeroLevelsVex ?? true, cex: indicators.zeroLevelsCex ?? true }}
          showGammaLevels={indicators.gammaLevels}
          gammaLevelToggles={{ gvwap: indicators.gammaLevelsGvwap ?? true, band: (indicators.gammaLevelsGvwap ?? true) && (indicators.gammaLevelsBand ?? true), walls: indicators.gammaLevelsWalls ?? true }}
          showZeroLevelsLive={indicators.oiData === 'live'}
          showExpectedMove={indicators.expectedMove}
          showForwardOi={indicators.showForwardOi}
          showOiDistribution={indicators.showOiDistribution}
          oiDistToggles={{ callOi: indicators.oiDistCallOi ?? true, putOi: indicators.oiDistPutOi ?? true, net: indicators.oiDistNet ?? true }}
          showOptionsVolume={indicators.showOptionsVolume}
          optVolToggles={{ callVol: indicators.optVolCalls ?? true, putVol: indicators.optVolPuts ?? true, net: indicators.optVolNet ?? true }}
          optVolCumulative={indicators.showOptionsVolume && indicators.optVolCumulative}
          optVolSplit={indicators.showOptionsVolume && indicators.optVolSplit}
          optVolFlowNet={indicators.showOptionsVolume && indicators.optVolSplit && indicators.optVolFlowNet}
          showVolFlowPane={indicators.showOptionsVolume && indicators.optVolFlowPane}
          showUnderlyingCompare={indicators.showUnderlyingCompare}
          underlyingCompareMode={indicators.underlyingCompareMode || 'oi'}
          underlyingCompareGreek={indicators.underlyingCompareGreek || 'gex'}
          underlyingCompareZeroLevels={indicators.underlyingCompareZeroLevels}
          underlyingCompareGammaLevels={indicators.underlyingCompareGammaLevels}
          optVolAnchorFrac={indicators.optVolAnchorFrac ?? null}
          optVolCumAnchorFrac={indicators.optVolCumAnchorFrac ?? null}
          showProjectedCurves={indicators.showProjectedCurves}
          projectedCurveGreeks={{ gex: indicators.projGex, dex: indicators.projDex, vex: indicators.projVex, cex: indicators.projCex }}
          projCurvesLive={indicators.oiData === 'live'}
          showExposureProfile={indicators.showExposureProfile}
          expProfileLiveSmile={indicators.oiData === 'live'}
          expProfileToggles={{ gex: indicators.expProfileGex ?? true, dex: indicators.expProfileDex ?? true, vex: indicators.expProfileVex ?? true, cex: indicators.expProfileCex ?? true }}
          exposureProfileMode={indicators.exposureProfileMode || 'dealer'}
          showExpProfileHeatmap={indicators.showExpProfileHeatmap}
          showExpProfileT1={indicators.showExpProfileT1}
          expProfileAnchorFrac={indicators.expProfileAnchorFrac ?? null}
          expProfileT1AnchorFrac={indicators.expProfileT1AnchorFrac ?? null}
          hedgeDemandAnchorFrac={indicators.hedgeDemandAnchorFrac ?? null}
          oiProfileAnchorFrac={indicators.oiProfileAnchorFrac ?? null}
          onProfileAnchorChange={handleIndicatorToggle}
          showExposure={indicators.showExposureProfile && indicators.exposure}
          exposureLiveSmile={indicators.oiData === 'live'}
          onLiveCurvesHealth={setLiveCurvesHealth}
          exposureMode={indicators.exposureProfileMode || 'dealer'}
          showHedgingDemand={indicators.hedgingDemand}
          showHedgeDemandHeatmap={indicators.showHedgeDemandHeatmap}
          hedgeDemandRealized={indicators.hedgeDemandRealized}
          showIvSkew={indicators.showIvSkew}
          ivSkewAnchorFrac={indicators.ivSkewAnchorFrac ?? null}
          ivSkewBand={indicators.showIvSkew && indicators.ivSkewBand !== false}
          showIvSkewShape={indicators.showIvSkew && indicators.ivSkewShape === true}
          showIVRealized={indicators.iv && indicators.ivRealized === true}
          showIV={indicators.iv}
          showIVStdDev={indicators.ivStdDev}
          quoteOverlays={quoteOverlays}
          thinCandles={true}
          candleBodyWidthPx={CANDLE_BODY_PX}
          barSpacing={BAR_SPACING}
        />
      </div>

      {/* Indicator Side Panel (curated planning config) */}
      <IndicatorSidePanel
        groups={PLANNING_GROUPS}
        storageKey="planningIndicatorPanel"
        defaultExpandedGroups={['profiles', 'volume']}
        indicators={indicators}
        onIndicatorToggle={handleIndicatorToggle}
        onApplyIndicators={handleApplyIndicators}
        greeksLoading={greeksLoading}
        onOpenChange={setPanelOpen}
        liveCurvesHealth={liveCurvesHealth}
      />
    </div>
  );
}
